import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import { MenuItems } from '../../../enums/MenuItems'
import Section from '../../common/sections/Section'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import roleApi from '../../../api/role/RoleApi'
import { Pages } from '../../../Pages'
import Form from '../../common/form/Form'
import TextInput from '../../common/form/inputs/TextInput'
import DropDown, { Item } from '../../common/form/inputs/DropDown'
import GetCountryFlag from '../../common/location/GetCountryFlag'
import ContractTypeBadge from '../ContractTypeBadge'
import { ContractType } from '../../../enums/ContractType'
import TextArea from '../../common/form/inputs/TextArea'
import { CreateRoleReq } from '../../../api/role/models/createRole/CreateRoleReq'
import { UK_CITIES } from '../../../constants/cities'
import { faArrowAltCircleRight } from '@fortawesome/free-solid-svg-icons'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'

// Zod schema
const roleSchema = z.object({
    jobTitle: z.string().min(1, 'Job title is required'),
    jobDescription: z.string().min(1, 'Job description is required'),
    location: z.string().min(1, 'Location is required'),
    contractType: z.string().min(1, 'Contract type is required'),
})

type RoleFormData = z.infer<typeof roleSchema>

export default function CreateRole(): React.JSX.Element {
    const location = useLocation()
    const navigate = useNavigate()
    const [apiError, setApiError] = useState<string | undefined>(undefined)
    const hideCancelButton = Boolean(
        (location.state as { hideCancel?: boolean } | null)?.hideCancel,
    )

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setValue,
        watch,
    } = useForm<RoleFormData>({
        resolver: zodResolver(roleSchema),
        defaultValues: {
            location: 'United Kingdom',
            contractType: 'permanent',
        },
    })

    const selectedLocation = watch('location')
    const selectedContractType = watch('contractType')

    // ✅ Build dropdown items
    const locationItems: Item[] = UK_CITIES.map((city) => ({
        id: city,
        content: (
            <div className="flex items-center gap-2">
                <GetCountryFlag location={city} showLabel={false} />
                <span>{city}</span>
            </div>
        ),
    }))

    const contractTypeItems: Item[] = [
        {
            id: 'permanent',
            content: (
                <ContractTypeBadge contractType={ContractType.Permanent} />
            ),
        },
        {
            id: 'contract',
            content: <ContractTypeBadge contractType={ContractType.Contract} />,
        },
    ]

    // ✅ Resolve the selected item from the list
    const selectedLocationItem = locationItems.find(
        (i) => i.id === selectedLocation,
    ) || {
        id: '',
        content: <span className="text-gray-400">Select Location</span>,
    }

    const selectedContractItem = contractTypeItems.find(
        (i) => i.id === selectedContractType,
    ) || {
        id: '',
        content: <span className="text-gray-400">Select Contract Type</span>,
    }

    // Handlers
    const handleLocationChange = (item: Item) => {
        setValue('location', item.id)
    }

    const handleContractTypeChange = (item: Item) => {
        setValue('contractType', item.id)
    }

    // Submit
    const onSubmit = async (data: RoleFormData) => {
        setApiError(undefined)

        try {
            const req: CreateRoleReq = {
                jobTitle: data.jobTitle,
                jobDescription: data.jobDescription,
                location: data.location,
                contractType: data.contractType,
            }
            const roleRes = await roleApi.createRole(req)
            navigate(`${Pages.ViewRole}?id=${roleRes.roleId}`)
        } catch (error) {
            console.error(error)
            setApiError(
                error instanceof Error
                    ? error.message
                    : 'An unknown error occurred. Please try again later.',
            )
        }
    }

    // Focus on Job Title input field on component mount
    useEffect(() => {
        const jobTitleInput = document.getElementById(
            'jobTitle',
        ) as HTMLInputElement | null
        if (jobTitleInput) {
            jobTitleInput.focus()
        }
    }, [])

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section title="Create New Role" titleType="h2">
                <LoadingSpinner show={isSubmitting} fullScreen={true} />

                <Form
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    apiError={apiError}
                    entityName="role"
                    cancelTo={hideCancelButton ? undefined : Pages.Roles}
                    isSubmitting={isSubmitting}
                    className="mt-4"
                    submitButtonContent={(submitting) =>
                        submitting ? (
                            'Saving…'
                        ) : (
                            <>
                                Next
                                <FontAwesomeIcon
                                    icon={faArrowAltCircleRight}
                                    className="ml-2"
                                />
                            </>
                        )
                    }
                >
                    <TextInput
                        id="jobTitle"
                        name="Job Title"
                        placeholder="Job Title"
                        register={register}
                        errors={errors}
                    />

                    <DropDown
                        id="location"
                        selected={selectedLocationItem}
                        name="Location"
                        placeholder="Select Location"
                        onChange={handleLocationChange}
                        items={locationItems}
                        error={errors.location?.message}
                    />

                    <DropDown
                        id="contractType"
                        selected={selectedContractItem}
                        name="Contract Type"
                        placeholder="Select Contract Type"
                        onChange={handleContractTypeChange}
                        items={contractTypeItems}
                        error={errors.contractType?.message}
                    />

                    <Note type={NoteType.INFO}>
                        Add a lot of detail as this is used to find the best
                        candidates
                    </Note>
                    <TextArea
                        id="jobDescription"
                        name="Job Description"
                        placeholder="Paste a job description or describe the role..."
                        register={register}
                        errors={errors}
                        rows={10}
                    />
                </Form>
            </Section>
        </LayoutSingleView>
    )
}
